import { useCallback, useState, useSyncExternalStore } from 'react';
import { useMutation } from '@apollo/client';

import { useAuth } from 'components/Auth/AuthProvider';
import { UPGRADE_SMS_CONSENT } from 'apolloClient/mutations/alerts';
import {
  getbrowserUUIDFromLocalStorage,
  getLeadIdFromLocalStorage,
} from 'src/utils/utils';

// Session-level SMS consent. Sticky YES: once granted it NEVER flips back to No,
// and it is shared across every useSmsConsent() instance so any later form/popup
// sees it — including for logged-out visitors, who have no `me` to write to.
// Backed by sessionStorage so it SURVIVES a page reload/hard navigation (the
// in-memory variable alone resets on reload). Cleared when the tab closes; the
// backend `me.smsConsent` (via GET_ME) is the durable cross-session source once
// that field is wired. NOTE: this flag only suppresses the popup — the submitted
// value stays the raw checkbox (see submitConsentValue), so a persisted flag can
// never make a different person's submit falsely record consent.
const SESSION_CONSENT_KEY = 'smsConsentGrantedThisSession';

const readStoredConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(SESSION_CONSENT_KEY) === 'true';
  } catch {
    return false;
  }
};

let sessionConsentYes = readStoredConsent();
const sessionConsentListeners = new Set<() => void>();
const sessionConsentStore = {
  isYes: () => sessionConsentYes,
  setYes: () => {
    if (!sessionConsentYes) {
      sessionConsentYes = true;
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(SESSION_CONSENT_KEY, 'true');
        } catch {
          // ignore storage errors (private mode / disabled storage)
        }
      }
      sessionConsentListeners.forEach((listener) => listener());
    }
  },
  subscribe: (listener: () => void) => {
    sessionConsentListeners.add(listener);
    return () => {
      sessionConsentListeners.delete(listener);
    };
  },
};

export type SmsConsentUpgradeKeys = {
  email?: string;
  phone?: string;
  // The sendContactUsForm response returns leadId as a number; coerced to
  // String before it's sent to upgradeSmsConsent (GraphQL arg is String).
  leadId?: string | number | null;
};

/**
 * SMS consent (A2P 10DLC) state machine shared by the lead forms.
 *
 * If consent is already granted, the checkbox is HIDDEN everywhere (and no popup
 * shows). Otherwise the checkbox is shown, empty by default; leaving it unchecked
 * on submit triggers the follow-up "one more thing" popup.
 *
 * `alreadyConsented` is true when the account has consent (`me.smsConsent === true`,
 * checked strictly — never truthiness; undefined until the backend exposes it) OR
 * consent was granted this session (via markConsented, sticky + persisted to
 * sessionStorage so it survives reloads). Never-downgrade (an existing Yes is never
 * overwritten by a later No) is enforced by the backend.
 */
export function useSmsConsent() {
  const { me, isLogged, setMe } = useAuth();

  const sessionConsentSynced = useSyncExternalStore(
    sessionConsentStore.subscribe,
    sessionConsentStore.isYes,
    () => false
  );

  // Consent already granted — persisted on the account (page-load), or granted
  // this session (works logged-out too).
  const alreadyConsented =
    (isLogged && me?.smsConsent === true) || sessionConsentSynced;
  // Hide the checkbox once consent exists — in every form + registration.
  const isCheckboxVisible = !alreadyConsented;

  const [checked, setChecked] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  const [upgradeSmsConsent] = useMutation(UPGRADE_SMS_CONSENT);

  // Once consent is granted (checkbox=yes at submit, or popup "Yes"), record it
  // so no form/popup on the page prompts again. The session store is the source
  // of truth (sticky Yes, survives logged-out + me refreshes); we also mirror it
  // onto `me` for logged-in users so it's there for the account.
  const markConsented = useCallback(() => {
    sessionConsentStore.setYes();
    if (me && me.smsConsent !== true) {
      setMe({ ...me, smsConsent: true });
    }
  }, [me, setMe]);

  // If the checkbox is hidden the user already consented, so consent is YES;
  // otherwise send the raw checkbox value.
  const submitConsentValue = isCheckboxVisible ? checked : true;

  // Prompt with the popup only when the checkbox is shown (not yet consented) and
  // was left unchecked.
  const shouldPromptAfterSubmit = isCheckboxVisible && !checked;

  const openPopup = useCallback(() => setPopupOpen(true), []);
  const onPopupDismiss = useCallback(() => setPopupOpen(false), []);

  const onPopupYes = useCallback(
    async (keys: SmsConsentUpgradeKeys) => {
      setPopupOpen(false);
      try {
        // leadId must be a String for the GraphQL arg, but the sendContactUsForm
        // response returns it as a number — coerce it (mirrors LoadUserAgents,
        // which sends `leadId || undefined`). Never send a number or null.
        const leadId = keys.leadId ?? getLeadIdFromLocalStorage();
        await upgradeSmsConsent({
          variables: {
            consent: true,
            email: keys.email,
            phone: keys.phone,
            leadId: leadId ? String(leadId) : undefined,
            browserUUID: getbrowserUUIDFromLocalStorage() || undefined,
          },
        });
        // Backend accepted the upgrade — reflect it locally so we don't re-prompt.
        markConsented();
      } catch (error) {
        // Fail gracefully — backend may not be ready and the inquiry is already
        // captured. Never block the success UI on the consent upgrade.
        console.error(error);
      }
    },
    [upgradeSmsConsent, markConsented]
  );

  const reset = useCallback(() => {
    setChecked(false);
    setPopupOpen(false);
  }, []);

  return {
    isCheckboxVisible,
    alreadyConsented,
    // Raw checkbox state — always empty by default, never pre-checked.
    checked,
    setChecked,
    submitConsentValue,
    shouldPromptAfterSubmit,
    popupOpen,
    openPopup,
    onPopupYes,
    onPopupDismiss,
    markConsented,
    reset,
  };
}
